/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect } from 'react';
import { Input, message, Modal } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { styleFn } from '~components/molecules/RightSideNav/Modals/ModalFormStyles.style';

import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { appLog } from '~src/utils/log';
import { queryCache, useMutation } from 'react-query';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { useStyles } from '~src/hooks/useStyles';
import { updateSecondaryShareKey } from '~src/services/api/secondaryVendors';
import { Controller, useForm } from 'react-hook-form';

export type EditSecondaryShareKeyModalProps = Pick<ModalProps, 'visible'> & {
  onCancel?: () => void;
  shareId: string;
  shareKey: string;
  secondaryShareKey: string;
  emailDisabled?: boolean;
  previousData?: {
    email?: string;
    title?: string;
  };
};

export const EditSecondaryShareKeyModal: React.FC<EditSecondaryShareKeyModalProps> = ({
  onCancel,
  shareId,
  shareKey,
  secondaryShareKey,
  emailDisabled,
  previousData,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const sharesForm = useForm({
    defaultValues: {
      email: '',
      title: '',
    },
  });

  const [updateShareMutation, { isLoading }] = useMutation(
    updateSecondaryShareKey,
    {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.secondaryShareKeys]);
        message.success(intl.formatMessage({ id: 'success' }));
      },
      onError: (error, item) => {
        message.error(intl.formatMessage({ id: 'share-edit-failed' }));
        appLog('updateShareMutation -> item', item);
        appLog('updateShareMutation -> err', error);
      },
    }
  );

  const handleResetForm = () => {
    sharesForm.reset();
  };

  const isValid = () => {
    const { email, title } = sharesForm.getValues();
    if (!email || !title) {
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!isValid()) return;

      await updateShareMutation({
        shareId,
        shareKey,
        secondaryShareKey,
        data: sharesForm.getValues(),
      });

      handleResetForm();

      onCancel?.();
    } catch (err) {
      message.error(intl.formatMessage({ id: 'share-edit-failed' }));
    }
  };

  useEffect(() => {
    const values = sharesForm.getValues();
    if (previousData && !values.email) {
      sharesForm.reset({
        email: previousData.email,
        title: previousData.title,
      });
    }
  }, [previousData]);

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({
            id: 'secondary-share-key-edit-modal-title',
          })}
          icon={['far', 'pen']}
        />
      }
      okText={intl.formatMessage({ id: 'edit' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      centered
      okButtonProps={{
        loading: isLoading,
      }}
      onOk={handleSubmit}
      onCancel={() => {
        handleResetForm();
        onCancel?.();
      }}
      destroyOnClose
      zIndex={2000}
      width={500}
      {...modalProps}>
      <div css={styles.formContainer}>
        <div css={styles.formItem}>
          <p css={styles.formLabel}>{intl.formatMessage({ id: 'email' })}</p>
          <Controller
            name="email"
            control={sharesForm.control}
            render={({ field }) => (
              <Input disabled={emailDisabled} {...field} />
            )}
          />
        </div>
        <div css={styles.formItem}>
          <p css={styles.formLabel}>{intl.formatMessage({ id: 'title' })}</p>
          <Controller
            name="title"
            control={sharesForm.control}
            render={({ field }) => <Input {...field} />}
          />
        </div>
      </div>
    </Modal>
  );
};
